import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { AppShell } from "../components/AppShell";
import { useAuth } from "../context/AuthContext";
import { allowedSites } from "../lib/sites";
import { permitMeta } from "../lib/permitSchemas";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Search, FilePlus2, CheckCircle2, XCircle, MapPin, Loader2, Download } from "lucide-react";

function fmtDateTime(dt) {
  if (!dt) return "—";
  try {
    return new Date(dt).toLocaleString("en-GB", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return dt;
  }
}

const ACTIONS = {
  issued: { label: "Issued", icon: FilePlus2, cls: "bg-blue-100 text-blue-700" },
  signed_off: { label: "Signed off", icon: CheckCircle2, cls: "bg-green-100 text-green-700" },
  cancelled: { label: "Cancelled", icon: XCircle, cls: "bg-red-100 text-red-700" },
};

const ALL = "__all__";

export default function AuditLog() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [events, setEvents] = useState(null);
  const [search, setSearch] = useState("");
  const [site, setSite] = useState(ALL);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [exporting, setExporting] = useState(false);

  const sites = useMemo(() => allowedSites(user), [user]);

  const buildParams = () => {
    const params = {};
    if (search) params.search = search;
    if (site !== ALL) params.site = site;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    return params;
  };

  const load = async () => {
    setEvents(null);
    const { data } = await api.get("/audit", { params: buildParams() });
    setEvents(data);
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await api.get("/audit/export", { params: buildParams(), responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `audit-log-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, site, dateFrom, dateTo]);

  return (
    <AppShell>
      <div className="mb-8">
        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-brand">GFM - Permit to Work Management System</div>
        <h1 className="font-heading text-3xl font-bold tracking-tight text-foreground">Audit log</h1>
        <p className="text-sm text-muted-foreground">Every permit issued, signed off or cancelled — who did it and when.</p>
      </div>

      <div className="rounded-md border border-border bg-white shadow-sm">
        <div className="flex flex-wrap items-center gap-3 border-b border-border p-4">
          <div className="flex flex-1 items-center gap-2">
            <Search className="h-4 w-4 text-muted-foreground" />
            <Input
              data-testid="audit-search"
              className="border-0 shadow-none focus-visible:ring-0"
              placeholder="Search by contractor, company, permit number or user…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="w-64">
            <Select value={site} onValueChange={setSite}>
              <SelectTrigger data-testid="audit-site-filter">
                <SelectValue placeholder="All sites" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All sites</SelectItem>
                {sites.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-40" data-testid="audit-date-from" title="From date" />
            <span className="text-muted-foreground">–</span>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-40" data-testid="audit-date-to" title="To date" />
            {(dateFrom || dateTo) && (
              <Button variant="ghost" size="sm" onClick={() => { setDateFrom(""); setDateTo(""); }} data-testid="audit-clear-dates">Clear</Button>
            )}
          </div>
          <Button variant="outline" onClick={exportCsv} disabled={exporting} data-testid="audit-export-btn">
            {exporting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />} Export CSV
          </Button>
        </div>

        {events === null ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>
        ) : events.length === 0 ? (
          <div className="px-4 py-12 text-center text-muted-foreground" data-testid="audit-empty">No matching activity.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="audit-table">
              <thead className="bg-secondary text-left text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-4 py-3">Action</th>
                  <th className="px-4 py-3">Permit</th>
                  <th className="px-4 py-3">Contractor</th>
                  <th className="px-4 py-3">Site</th>
                  <th className="px-4 py-3">By</th>
                  <th className="px-4 py-3">When</th>
                </tr>
              </thead>
              <tbody>
                {events.map((e, i) => {
                  const a = ACTIONS[e.action] || { label: e.action, icon: FilePlus2, cls: "bg-neutral-100 text-neutral-600" };
                  const Icon = a.icon;
                  return (
                    <tr
                      key={`${e.permit_id}-${e.action}-${i}`}
                      data-testid={`audit-row-${e.permit_id}-${e.action}`}
                      onClick={() => navigate(`/permits/${e.permit_id}`)}
                      className="cursor-pointer border-t border-border transition-colors hover:bg-secondary/50"
                    >
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${a.cls}`}>
                          <Icon className="h-3 w-3" /> {a.label}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-semibold text-brand">{e.permit_number}</div>
                        <div className="mt-0.5 flex gap-1">
                          {(e.permit_types || []).map((t) => (
                            <span key={t} className="rounded px-1 py-0.5 text-[10px] font-semibold text-white" style={{ background: permitMeta(t)?.color }} title={permitMeta(t)?.title}>
                              {permitMeta(t)?.short}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium">{e.contractor_name}</div>
                        <div className="text-xs text-muted-foreground">{e.company}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                          <MapPin className="h-3 w-3 text-brand" /> {e.site_location || "—"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{e.by || "—"}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">{fmtDateTime(e.at)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AppShell>
  );
}
